import axios from "axios";
import type {
  AxiosHeaderValue,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { apiConfig, graphConfig } from "config/app-config";
import type { SDEApi } from "config/app-config";
import { networkService } from "services";

function getConfigWithHeaders(
  scopes: Array<string>,
  additionalHeaders?: Record<string, AxiosHeaderValue | undefined>
): (config: InternalAxiosRequestConfig<unknown>) => Promise<InternalAxiosRequestConfig<unknown>> {
  return async (config: InternalAxiosRequestConfig<unknown>) => {
    const defaultHeaders = await networkService.createHeaders(scopes);
    // Supprimer le Content-Type si la requête contient un FormData, Axios s'en chargera sous le capot
    if (config.data instanceof FormData) {
      delete defaultHeaders["Content-Type"];
    }
    const headers = Object.entries({ ...defaultHeaders, ...additionalHeaders });
    for (const [name, value] of headers) {
      config.headers.set(name, value);
    }
    return config;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getResponseData(response: AxiosResponse<any, any>): any {
  return response?.data?.data ?? response?.data ?? response;
}

/**
 * Créer une instance Axios avec la configuration des headers et middleware
 * pour une utilisation d'API Sdeapps _standard_.
 */
function createSdeappsAxiosClient({ scopes, apiName }: SDEApi): AxiosInstance {
  const sdeappsApiClient = axios.create({
    baseURL: `${apiConfig.apimBaseUrl}/${apiName}`,
  });
  sdeappsApiClient.interceptors.request.use(getConfigWithHeaders(scopes));
  sdeappsApiClient.interceptors.response.use(getResponseData);
  return sdeappsApiClient;
}

// API Collectivités
const collectivitesApiClient = createSdeappsAxiosClient(apiConfig.collectivitesApi);
// collectivitesApiClient.defaults.baseURL = "http://localhost:7108/api";

// API Élus
const elusApiClient = createSdeappsAxiosClient(apiConfig.elusApi);
// elusApiClient.defaults.baseURL = "http://localhost:7108/api";

// API Établissements publics
const etablissementsPublicsApiClient = createSdeappsAxiosClient(apiConfig.etablissementsPublicsApi);
// etablissementsPublicsApiClient.defaults.baseURL = "http://localhost:7108/api";

// API Contacts Métiers
const cometApiClient = createSdeappsAxiosClient(apiConfig.cometApi);
// cometApiClient.defaults.baseURL = "http://localhost:7108/api";

// API Relation Usagers Clients
const rucApiClient = createSdeappsAxiosClient(apiConfig.rucApi);
// rucApiClient.defaults.baseURL = "http://localhost:7210/api";

// API Graph
const graphApiName = graphConfig.graphEndpoint;
const graphApiScopes = apiConfig.azureAd.scopes;
const graphApiClient = axios.create({ baseURL: graphApiName });
graphApiClient.interceptors.request.use(
  getConfigWithHeaders(graphApiScopes, { ConsistencyLevel: "eventual" })
);
graphApiClient.interceptors.response.use(getResponseData);

export {
  collectivitesApiClient,
  elusApiClient,
  etablissementsPublicsApiClient,
  cometApiClient,
  rucApiClient,
  graphApiClient,
};
